import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, MessageCircle, Calendar, CreditCard, CheckCircle, XCircle, 
  Loader, Check, Trash2, RefreshCw 
} from 'lucide-react';
import { notificationService } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

interface Notification {
  id: number;
  type: string;
  titre: string;
  message: string;
  is_read: boolean;
  action_url?: string;
  created_at: string;
  data?: any;
}

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationService.getAll({ per_page: 50 });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error(error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await notificationService.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notification supprimée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    
    // Pour les notifications de message, toujours rediriger vers les messages
    if (notification.type === 'new_message') {
      // Construire l'URL vers les messages selon le rôle de l'utilisateur
      const basePath = user?.role === 'ROLE_GERANT' ? '/gerant/messages' : '/locataire/messages';
      
      if (notification.data?.sender_id) {
        navigate(`${basePath}?user=${notification.data.sender_id}`);
      } else {
        navigate(basePath);
      }
      return;
    }
    
    // Pour les autres types de notifications
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'new_message':
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'new_booking':
        return <Calendar className="w-5 h-5 text-green-500" />;
      case 'booking_confirmed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'booking_cancelled':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'payment_received':
        return <CreditCard className="w-5 h-5 text-emerald-500" />;
      case 'booking_reminder':
        return <Bell className="w-5 h-5 text-orange-500" />;
      default:
        return <Bell className="w-5 h-5 text-primary-500" />;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Bell className="w-6 h-6" />
            Notifications
          </h1>
          {unreadCount > 0 && (
            <span className="bg-primary-500 text-white text-sm px-2 py-0.5 rounded-full">
              {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchNotifications}
            className="btn btn-outline btn-sm flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="btn btn-outline btn-sm flex items-center gap-1"
            >
              <Check className="w-4 h-4" />
              Tout marquer lu
            </button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="card p-8 text-center">
          <Bell className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">Aucune notification pour le moment.</p>
        </div>
      ) : (
        <div className="card divide-y divide-neutral-100">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`p-4 flex items-start gap-4 transition-colors ${
                !notification.is_read ? 'bg-primary-50/50' : 'hover:bg-neutral-50'
              } ${notification.action_url ? 'cursor-pointer' : ''}`}
              onClick={() => handleClick(notification)}
            >
              <div className={`p-2 rounded-full ${!notification.is_read ? 'bg-white shadow-sm' : 'bg-neutral-100'}`}>
                {getIcon(notification.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className={`font-medium ${!notification.is_read ? 'text-neutral-900' : 'text-neutral-700'}`}>
                      {notification.titre}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-0.5 whitespace-pre-line">
                      {notification.message}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0 mt-2"></span>
                  )}
                </div>
                <p className="text-xs text-neutral-400 mt-2">
                  {formatDate(notification.created_at)}
                </p>
              </div>
              
              <div className="flex items-center gap-1">
                {!notification.is_read && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(notification.id);
                    }}
                    className="p-1.5 text-neutral-400 hover:text-primary-500 hover:bg-neutral-100 rounded"
                    title="Marquer comme lu"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(notification.id);
                  }}
                  className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-neutral-100 rounded"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
